import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Play, Plus, Save, X, Edit3, Trash2, ArrowLeft, ExternalLink, Megaphone, Sparkles, TrendingUp, Target, Users, ChevronRight, FolderPlus, Layers } from "lucide-react";

const convertToEmbedUrl = (url) => {
  if (!url) return "";
  if (url.includes("/embed/") || url.includes("/preview")) return url;
  const driveMatch = url.match(/\/d\/([^\/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  const driveIdMatch = url.match(/[?&]id=([^&]+)/);
  if (driveIdMatch) return `https://drive.google.com/file/d/${driveIdMatch[1]}/preview`;
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const shortsMatch = url.match(/shorts\/([^?&]+)/);
  if (shortsMatch) videoId = shortsMatch[1];
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const FEATURE_ICONS = [Sparkles, TrendingUp, Target, Users];

export default function ServiziCategoria() {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDM = slug === "digital-marketing";

  // DM state
  const [dmDesc, setDmDesc] = useState(null);
  const [dmFeatures, setDmFeatures] = useState([]);
  const [dmLink, setDmLink] = useState(null);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "" });
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", url: "" });
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({ title: "" });
  const [editingSubId, setEditingSubId] = useState(null);
  const [editSubForm, setEditSubForm] = useState({ title: "" });

  useEffect(() => { fetchContent(); }, [slug]);

  const fetchContent = async () => {
    try {
      const [slotsRes, subsRes, videosRes] = await Promise.all([
        axios.get(`${API}/content/servizi-slots`),
        axios.get(`${API}/content/servizi-subcategories`),
        axios.get(`${API}/content/servizi-videos`)
      ]);

      setCategory(slotsRes.data.find(s => s.content === slug));

      // Parse subcategories for this category
      const allSubs = subsRes.data.map(s => {
        try { return { ...s, ...JSON.parse(s.content) }; }
        catch { return s; }
      });
      setSubcategories(allSubs.filter(s => s.parent_slug === slug).sort((a, b) => a.order - b.order));

      // Get videos that belong directly to this category (no subcategory)
      setVideos(videosRes.data.filter(v => {
        try {
          const parsed = JSON.parse(v.content);
          return parsed.slot === slug && !parsed.subcategory;
        } catch { return false; }
      }));

      if (isDM) {
        const dmRes = await axios.get(`${API}/content/digital-marketing`);
        if (dmRes.data.length === 0) {
          await axios.post(`${API}/init-dm`);
          const dmRes2 = await axios.get(`${API}/content/digital-marketing`);
          setDmDesc(dmRes2.data.find(d => d.content_type === "text") || null);
          setDmFeatures(dmRes2.data.filter(d => d.content_type === "feature").sort((a, b) => a.order - b.order));
          setDmLink(dmRes2.data.find(d => d.content_type === "link") || null);
        } else {
          setDmDesc(dmRes.data.find(d => d.content_type === "text") || null);
          setDmFeatures(dmRes.data.filter(d => d.content_type === "feature").sort((a, b) => a.order - b.order));
          setDmLink(dmRes.data.find(d => d.content_type === "link") || null);
        }
      }
    } catch (e) { console.error("Error:", e); }
    finally { setLoading(false); }
  };

  const getVideoUrl = (video) => {
    try { return JSON.parse(video.content).url; }
    catch { return convertToEmbedUrl(video.content); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ title: item.title || "", content: item.content || "" });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/content/${id}`, { title: editForm.title, content: editForm.content });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const saveVideoEdit = async (id) => {
    try {
      await axios.put(`${API}/content/${id}`, {
        title: editForm.title,
        content: JSON.stringify({ url: convertToEmbedUrl(editForm.content), slot: slug })
      });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminato");
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const addVideo = async () => {
    if (!newVideo.url.trim()) { toast.error("Inserisci un URL video"); return; }
    try {
      await axios.post(`${API}/content`, {
        section: "servizi-videos",
        content_type: "video",
        title: newVideo.title || "Nuovo Video",
        content: JSON.stringify({ url: convertToEmbedUrl(newVideo.url), slot: slug }),
        order: videos.length
      });
      toast.success("Video aggiunto");
      setNewVideo({ title: "", url: "" });
      setShowAddVideo(false);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  // Subcategory CRUD
  const addSubcategory = async () => {
    if (!newSub.title.trim()) { toast.error("Inserisci un titolo"); return; }
    const subSlug = newSub.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    try {
      await axios.post(`${API}/content`, {
        section: "servizi-subcategories",
        content_type: "subcategory",
        title: newSub.title,
        content: JSON.stringify({ parent_slug: slug, slug: subSlug }),
        order: subcategories.length
      });
      toast.success("Sottocategoria aggiunta");
      setNewSub({ title: "" });
      setShowAddSub(false);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const startEditSub = (sub, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingSubId(sub.id);
    setEditSubForm({ title: sub.title });
  };

  const saveSubEdit = async (sub) => {
    try {
      const newSlug = editSubForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      await axios.put(`${API}/content/${sub.id}`, {
        title: editSubForm.title,
        content: JSON.stringify({ parent_slug: slug, slug: newSlug })
      });
      toast.success("Sottocategoria aggiornata");
      setEditingSubId(null);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const deleteSub = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Sottocategoria eliminata");
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="page-container">
        <div className="container py-20 text-center">
          <p style={{ color: "var(--mm-text-secondary)" }}>Categoria non trovata</p>
          <Link to="/servizi" className="btn-primary inline-block mt-4">Torna ai Servizi</Link>
        </div>
      </div>
    );
  }

  const hasSubcategories = subcategories.length > 0;

  return (
    <div className="page-container trading-bg" data-testid="servizi-categoria-page">
      <PageHero image={HERO_IMAGES.servizi} kicker={isDM ? "Percorso Formativo" : "Formazione"} title={category.title} />
      <SectionBg type="trading" />
      <div className="container py-8 relative z-10">
        {/* Back */}
        <Link to="/servizi" className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all hover:bg-white/5" style={{ color: "var(--mm-gold)" }}>
          <ArrowLeft size={20} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Torna ai Servizi</span>
        </Link>

        {/* === DM CONTENT === */}
        {isDM && dmDesc && (
          <div className="max-w-3xl mx-auto mb-10" data-testid="dm-description">
            {editingId === dmDesc.id ? (
              <div className="glass-card p-5 space-y-4">
                <textarea value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="admin-textarea" rows={8} />
                <div className="flex gap-3">
                  <button onClick={() => saveEdit(dmDesc.id)} className="btn-primary flex-1 flex items-center justify-center gap-2"><Save size={16} /> Salva</button>
                  <button onClick={() => setEditingId(null)} className="btn-secondary flex-1">Annulla</button>
                </div>
              </div>
            ) : (
              <div className="relative group">
                {isAdmin && <button onClick={() => startEdit(dmDesc)} className="absolute top-2 right-2 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}><Edit3 size={16} style={{ color: "var(--mm-gold)" }} /></button>}
                <p className="text-base sm:text-lg leading-relaxed text-center whitespace-pre-line" style={{ color: "var(--mm-text-secondary)" }}>{dmDesc.content}</p>
              </div>
            )}
          </div>
        )}

        {isDM && dmFeatures.length > 0 && (
          <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mb-10" data-testid="dm-features">
            {dmFeatures.map((feature, index) => {
              const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length];
              if (editingId === feature.id) {
                return (
                  <div key={feature.id} className="glass-card p-4 space-y-3" style={{ borderColor: "rgba(47, 216, 168, 0.1)" }}>
                    <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input text-sm" placeholder="Titolo" />
                    <input type="text" value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="admin-input text-sm" placeholder="Sottotitolo" />
                    <div className="flex gap-2"><button onClick={() => saveEdit(feature.id)} className="btn-primary flex-1 py-1.5 text-sm">Salva</button><button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-1.5 text-sm">Annulla</button></div>
                  </div>
                );
              }
              return (
                <div key={feature.id} className="glass-card p-4 sm:p-5 relative group" style={{ borderColor: "rgba(47, 216, 168, 0.1)" }} data-testid={`dm-feature-${index}`}>
                  {isAdmin && <button onClick={() => startEdit(feature)} className="absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}><Edit3 size={12} style={{ color: "var(--mm-gold)" }} /></button>}
                  <Icon size={28} style={{ color: "var(--mm-gold)", marginBottom: "12px" }} />
                  <h3 className="text-sm sm:text-base font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{feature.title}</h3>
                  <p className="text-xs sm:text-sm" style={{ color: "var(--mm-text-secondary)" }}>{feature.content}</p>
                </div>
              );
            })}
          </div>
        )}

        {isDM && dmLink && (
          <div className="max-w-xl mx-auto mb-12" data-testid="dm-course-section">
            {editingId === dmLink.id ? (
              <div className="glass-card p-5 space-y-4">
                <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input" placeholder="Titolo link" />
                <input type="text" value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="admin-input" placeholder="URL corso" />
                <div className="flex gap-3"><button onClick={() => saveEdit(dmLink.id)} className="btn-primary flex-1 flex items-center justify-center gap-2"><Save size={16} /> Salva</button><button onClick={() => setEditingId(null)} className="btn-secondary flex-1">Annulla</button></div>
              </div>
            ) : (
              <a href={dmLink.content} target="_blank" rel="noopener noreferrer" className="glass-card p-6 sm:p-8 block group cursor-pointer transition-all duration-300 hover:scale-[1.02] gold-glow text-center relative" style={{ borderColor: "rgba(47, 216, 168, 0.3)", textDecoration: "none" }} data-testid="dm-course-link">
                {isAdmin && <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(dmLink); }} className="absolute top-3 right-3 p-2 rounded-lg z-10" style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}><Edit3 size={16} style={{ color: "var(--mm-gold)" }} /></button>}
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: "rgba(47, 216, 168, 0.15)" }}><Megaphone size={40} style={{ color: "var(--mm-gold)" }} /></div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-3 gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{dmLink.title}</h2>
                <p className="text-base mb-6" style={{ color: "var(--mm-text-secondary)" }}>Accedi al corso completo sulla piattaforma IQ Academy</p>
                <div className="inline-flex items-center gap-3 px-8 py-4 rounded-lg transition-all duration-300 group-hover:bg-[#2FD8A8] group-hover:text-black" style={{ backgroundColor: "rgba(47, 216, 168, 0.15)", border: "1px solid var(--mm-gold)", color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  ACCEDI AL CORSO <ExternalLink size={20} className="transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            )}
          </div>
        )}

        {/* === SUBCATEGORIES SECTION === */}
        {(hasSubcategories || isAdmin) && !isDM && (
          <>
            <div className="flex items-center gap-4 max-w-3xl mx-auto mb-6">
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
              <div className="flex items-center gap-2">
                <Layers size={16} style={{ color: "var(--mm-gold)" }} />
                <span className="text-sm font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>SOTTOCATEGORIE</span>
              </div>
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
            </div>

            {/* Admin Add Subcategory */}
            {isAdmin && (
              <div className="max-w-lg mx-auto mb-6">
                {!showAddSub ? (
                  <button onClick={() => setShowAddSub(true)} className="btn-primary flex items-center gap-2 mx-auto text-sm" data-testid="add-subcategory-btn">
                    <FolderPlus size={16} /> Aggiungi Sottocategoria
                  </button>
                ) : (
                  <div className="glass-card p-4 space-y-3">
                    <input type="text" value={newSub.title} onChange={(e) => setNewSub({ ...newSub, title: e.target.value })} className="admin-input" placeholder="Nome sottocategoria" data-testid="new-subcategory-input" />
                    <div className="flex gap-3">
                      <button onClick={addSubcategory} className="btn-primary flex-1 py-2 text-sm"><Save size={14} className="inline mr-1" />Salva</button>
                      <button onClick={() => { setShowAddSub(false); setNewSub({ title: "" }); }} className="btn-secondary flex-1 py-2 text-sm">Annulla</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Subcategories Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
              {subcategories.map((sub, index) => {
                if (editingSubId === sub.id) {
                  return (
                    <div key={sub.id} className="glass-card p-4 space-y-3" style={{ borderColor: "rgba(47, 216, 168, 0.2)" }}>
                      <input type="text" value={editSubForm.title} onChange={(e) => setEditSubForm({ ...editSubForm, title: e.target.value })} className="admin-input text-sm" />
                      <div className="flex gap-2">
                        <button onClick={() => saveSubEdit(sub)} className="btn-primary flex-1 py-1.5 text-sm">Salva</button>
                        <button onClick={() => setEditingSubId(null)} className="btn-secondary flex-1 py-1.5 text-sm">Annulla</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={sub.id}
                    to={`/servizi/${slug}/${sub.slug}`}
                    className="glass-card p-4 sm:p-5 flex flex-col items-center justify-center text-center group transition-all duration-300 hover:scale-[1.03] relative"
                    style={{ borderColor: "rgba(47, 216, 168, 0.15)", minHeight: "100px" }}
                    data-testid={`subcategory-${index}`}
                  >
                    {isAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button onClick={(e) => startEditSub(sub, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(47,216,168,0.2)" }}><Edit3 size={12} style={{ color: "var(--mm-gold)" }} /></button>
                        <button onClick={(e) => deleteSub(sub.id, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(239,68,68,0.2)" }}><Trash2 size={12} style={{ color: "#EF4444" }} /></button>
                      </div>
                    )}
                    <h3 className="text-base sm:text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{sub.title}</h3>
                    <div className="flex items-center gap-1 transition-all duration-300 group-hover:gap-2" style={{ color: "var(--mm-gold)" }}>
                      <span className="text-xs font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>APRI</span>
                      <ChevronRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}

        {/* === VIDEO SECTION (only if no subcategories or DM) === */}
        {(!hasSubcategories || isDM) && (
          <>
            {(isDM && (videos.length > 0 || isAdmin)) && (
              <div className="flex items-center gap-4 max-w-3xl mx-auto mb-8">
                <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
                <span className="text-sm font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>VIDEO</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
              </div>
            )}

            {isAdmin && (
              <div className="mb-8">
                {!showAddVideo ? (
                  <button onClick={() => setShowAddVideo(true)} className="btn-primary flex items-center gap-2 mx-auto" data-testid="add-video-btn">
                    <Plus size={18} /> Aggiungi Video
                  </button>
                ) : (
                  <div className="glass-card p-5 max-w-lg mx-auto space-y-4">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>Nuovo Video</h3>
                    <input type="text" value={newVideo.title} onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} className="admin-input" placeholder="Titolo video" />
                    <input type="text" value={newVideo.url} onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} className="admin-input" placeholder="Link YouTube o Google Drive" />
                    <div className="flex gap-3">
                      <button onClick={addVideo} className="btn-primary flex-1 flex items-center justify-center gap-2"><Save size={16} /> Salva</button>
                      <button onClick={() => { setShowAddVideo(false); setNewVideo({ title: "", url: "" }); }} className="btn-secondary flex-1">Annulla</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {videos.map((video, index) => (
                <div key={video.id} className="video-card" data-testid={`video-${index}`}>
                  {editingId === video.id ? (
                    <div className="p-4 space-y-3">
                      <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input" placeholder="Titolo" />
                      <input type="text" value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="admin-input" placeholder="Link video" />
                      <div className="flex gap-2">
                        <button onClick={() => saveVideoEdit(video.id)} className="btn-primary flex-1 py-2 text-sm">Salva</button>
                        <button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-2 text-sm">Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="video-container"><iframe src={getVideoUrl(video)} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen frameBorder="0" /></div>
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}><Play size={16} style={{ color: "var(--mm-gold)" }} /></div>
                          <h3 className="text-base sm:text-lg font-semibold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{video.title}</h3>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => { setEditingId(video.id); try { setEditForm({ title: video.title, content: JSON.parse(video.content).url }); } catch { setEditForm({ title: video.title, content: video.content }); } }} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(47, 216, 168, 0.1)" }}><Edit3 size={16} style={{ color: "var(--mm-gold)" }} /></button>
                            <button onClick={() => deleteItem(video.id)} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}><Trash2 size={16} style={{ color: "#EF4444" }} /></button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {videos.length === 0 && !isDM && !hasSubcategories && (
              <div className="glass-card p-12 text-center max-w-md mx-auto">
                <Play size={48} style={{ color: "var(--mm-gold)", margin: "0 auto 16px" }} />
                <p style={{ color: "var(--mm-text-secondary)" }}>{isAdmin ? "Aggiungi il primo video o crea delle sottocategorie" : "I video verranno aggiunti a breve"}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
